import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Download, Trash2, Shield, Edit3, Loader2, ArrowLeft, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../lib/api';

const MyDataPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [editName, setEditName] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const getHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const downloadMyData = async () => {
    setLoading('download');
    try {
      const r = await fetch(`${API_URL}/api/user/my-data`, { headers: getHeaders(), credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `my-data-${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Your data has been downloaded');
      } else {
        toast.error('Failed to fetch your data');
      }
    } catch (e) { toast.error('Failed to download data'); }
    setLoading(null);
  };

  const updateMyName = async () => {
    if (!editName.trim()) return;
    setLoading('edit');
    try {
      const r = await fetch(`${API_URL}/api/user/my-data`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName.trim() })
      });
      if (r.ok) {
        toast.success('Name updated');
        setEditName('');
      } else { toast.error('Failed to update'); }
    } catch (e) { toast.error('Update failed'); }
    setLoading(null);
  };

  const deleteMyData = async () => {
    if (deleteConfirm !== 'DELETE') { toast.error('Please type DELETE to confirm'); return; }
    setLoading('delete');
    try {
      const r = await fetch(`${API_URL}/api/user/my-data`, {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include'
      });
      if (r.ok) {
        toast.success('Your data has been deleted');
        setTimeout(() => { logout(); }, 2000);
      } else { toast.error('Failed to delete data'); }
    } catch (e) { toast.error('Deletion failed'); }
    setLoading(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Header />
        <main className="pt-24 pb-16 max-w-2xl mx-auto px-4 text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Sign in to manage your data</h1>
          <p className="text-slate-500 mb-4">You need to be logged in to access your privacy settings.</p>
          <Button onClick={() => navigate('/login')} className="bg-emerald-500 hover:bg-emerald-600 text-white">Sign In</Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-serif font-bold text-slate-800">Your Data & Privacy</h1>
              <p className="text-sm text-slate-500">Manage your personal data in accordance with GDPR</p>
            </div>
          </div>

          {/* Current Info */}
          <div className="bg-white rounded-2xl border p-6 mb-6">
            <h2 className="font-bold text-slate-800 mb-4">Your Account</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="font-medium">{user?.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium">{user?.email}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Membership</span><span className="font-medium capitalize">{user?.tier}</span></div>
            </div>
          </div>

          {/* Download Data */}
          <div className="bg-white rounded-2xl border p-6 mb-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-800">Download Your Data</h3>
                <p className="text-sm text-slate-500 mb-3">Get a copy of all your personal data including your profile, reading progress, bookmarks, journal entries, and orders.</p>
                <Button onClick={downloadMyData} disabled={loading === 'download'} variant="outline" className="rounded-full">
                  {loading === 'download' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Download My Data (JSON)
                </Button>
              </div>
            </div>
          </div>

          {/* Edit Data */}
          <div className="bg-white rounded-2xl border p-6 mb-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Edit3 className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-800">Update Your Information</h3>
                <p className="text-sm text-slate-500 mb-3">Correct your personal details.</p>
                <div className="flex gap-2">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="New display name" className="max-w-xs" />
                  <Button onClick={updateMyName} disabled={loading === 'edit' || !editName.trim()} variant="outline" className="rounded-full">
                    {loading === 'edit' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Delete Data */}
          <div className="bg-red-50 rounded-2xl border border-red-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-800">Delete All My Data</h3>
                <p className="text-sm text-red-600 mb-3">
                  Permanently delete your account and all associated data. This includes your profile, reading progress, bookmarks, journal entries, and discussion posts. <strong>This action cannot be undone.</strong>
                </p>
                {!showDelete ? (
                  <Button onClick={() => setShowDelete(true)} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-100">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete My Account & Data
                  </Button>
                ) : (
                  <div className="bg-white rounded-xl p-4 border border-red-200 space-y-3">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      <p className="font-medium text-sm">Type DELETE to confirm</p>
                    </div>
                    <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="Type DELETE" className="border-red-300" />
                    <div className="flex gap-2">
                      <Button onClick={() => { setShowDelete(false); setDeleteConfirm(''); }} variant="outline" size="sm">Cancel</Button>
                      <Button onClick={deleteMyData} disabled={deleteConfirm !== 'DELETE' || loading === 'delete'} size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                        {loading === 'delete' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Permanently Delete Everything
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400 text-center mt-6">
            Your rights under GDPR: access, rectification, erasure, and data portability. For questions, contact info@creassion.org
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyDataPage;
